// file: components/AreaPicker.jsx
//
// The exact area-selection UX from FilterPage.js (search box with live
// suggestions, removable chips, checkbox-pill grid with show more/less),
// extracted so other pages (e.g. Saved Search / My Areas) can offer the same
// professional, familiar experience instead of a bare list of buttons.
// Reuses FilterPage.css's classes so the look is pixel-identical.
import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import "./FilterPage.css";
import { fetchAreas } from "../common/areasApi";
import { useUserProfile, DEFAULT_PROPERTY_CITIES } from "../contexts/UserProfileContext";

/**
 * @param {string} city - currently selected city
 * @param {(city: string) => void} [onCityChange] - omit to hide the city row
 * @param {string[]} areas - currently selected area names
 * @param {(next: string[]) => void} onAreasChange
 * @param {string} [moduleType] - passed through to fetchAreas (default "requirement")
 * @param {number} [maxAreas] - optional cap on selections
 * @param {() => void} [onMaxAreasExceeded] - called instead of adding past the cap
 */
const AreaPicker = ({
  city,
  onCityChange,
  areas: selectedAreas,
  onAreasChange,
  moduleType = "requirement",
  maxAreas,
  onMaxAreasExceeded,
}) => {
  const { hasCityAccess, propertyAccessCities } = useUserProfile();
  // Driven by PropertyAccessCity, like the city rows on Owner/Broker Properties:
  // a user with ["Pune"] sees only Pune. Default list until the profile resolves.
  const cityOptions = propertyAccessCities.length ? propertyAccessCities : DEFAULT_PROPERTY_CITIES;
  const [areaOptions, setAreaOptions] = useState([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isShowingAllAreas, setIsShowingAllAreas] = useState(false);
  const [visibleAreasCount, setVisibleAreasCount] = useState(
    typeof window !== "undefined" && window.innerWidth < 768 ? 14 : 35
  );
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingAreas(true);
    setAreaOptions([]);
    fetchAreas(city, moduleType).then((names) => {
      if (!cancelled) {
        setAreaOptions(names || []);
        setIsLoadingAreas(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [city, moduleType]);

  useEffect(() => {
    const handleResize = () => setVisibleAreasCount(window.innerWidth < 768 ? 14 : 35);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addArea = (area) => {
    if (!area || selectedAreas.includes(area)) return;
    if (maxAreas && selectedAreas.length >= maxAreas) {
      onMaxAreasExceeded?.();
      return;
    }
    onAreasChange([...selectedAreas, area]);
  };

  const removeArea = (area) => {
    onAreasChange(selectedAreas.filter((a) => a !== area));
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(e.target.value.length > 0);
    setHighlightedIndex(-1);
  };

  const filteredAreas = areaOptions.filter(
    (area) =>
      area.toLowerCase().includes(searchQuery.toLowerCase()) && !selectedAreas.includes(area)
  );

  const handleSuggestionClick = (area) => {
    addArea(area);
    setSearchQuery("");
    setShowSuggestions(false);
    setHighlightedIndex(-1);
    searchInputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredAreas.length === 0) {
      if (e.key === "Enter" && searchQuery) {
        const matchingArea = areaOptions.find(
          (area) => area.toLowerCase() === searchQuery.toLowerCase()
        );
        if (matchingArea) {
          addArea(matchingArea);
          setSearchQuery("");
        }
        setShowSuggestions(false);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev === filteredAreas.length - 1 ? 0 : prev + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? filteredAreas.length - 1 : prev - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex !== -1) handleSuggestionClick(filteredAreas[highlightedIndex]);
    } else if (e.key === "Backspace" && searchQuery === "") {
      if (selectedAreas.length > 0) removeArea(selectedAreas[selectedAreas.length - 1]);
    }
  };

  const displayedAreas = isShowingAllAreas ? areaOptions : areaOptions.slice(0, visibleAreasCount);

  return (
    <div>
      {onCityChange && (
        <div className="filter-options1 flex-wrap" style={{ marginBottom: 12 }}>
          {cityOptions.map((cityOption) => {
            const cityAllowed = hasCityAccess ? hasCityAccess(cityOption) : true;
            return (
              <label
                key={cityOption}
                className={`radio-button ${city === cityOption ? "selected" : ""} ${
                  !cityAllowed ? "opacity-50 cursor-not-allowed" : ""
                }`}
                title={!cityAllowed ? "No access to this city" : undefined}
              >
                <input
                  type="radio"
                  name="areaPickerCity"
                  value={cityOption}
                  checked={city === cityOption}
                  disabled={!cityAllowed}
                  onChange={() => onCityChange(cityOption)}
                />
                {cityOption}
              </label>
            );
          })}
        </div>
      )}

      <div className="filter-options">
        <div className="area-search-container gap-3" ref={containerRef}>
          <div className="w-full flex items-center justify-end">
            <input
              type="text"
              ref={searchInputRef}
              placeholder={selectedAreas.length > 0 ? "Add more areas..." : "Search areas..."}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              className="w-full h-10 placeholder:p-0.5 p-3 shadow-sm border border-purple-800 shadow-slate-200 rounded-full relative my-2"
            />
          </div>

          {selectedAreas.map((area) => (
            <div
              key={area}
              className="area-chip inline-flex items-center justify-between bg-transparent text-purple-800 border border-purple-800"
            >
              <span className="truncate font-sans font-medium">{area}</span>
              <button
                type="button"
                className="remove-area-button text-white ml-2 flex-shrink-0"
                onClick={() => removeArea(area)}
              >
                &times;
              </button>
            </div>
          ))}

          {showSuggestions && searchQuery && (
            <div className="area-suggestions">
              {filteredAreas.length === 0 ? (
                <div className="suggestion-item text-gray-400">No matching areas</div>
              ) : (
                filteredAreas.map((area, index) => (
                  <div
                    key={area}
                    className={`suggestion-item ${index === highlightedIndex ? "highlighted" : ""}`}
                    onClick={() => handleSuggestionClick(area)}
                  >
                    {area}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <hr className="relative w-full left-0 md:w-[100%] md:left-[10px]" />

        {isLoadingAreas && (
          <div className="w-full text-center text-[13px] font-sans font-medium text-gray-500 py-2">
            Loading areas…
          </div>
        )}

        {!isLoadingAreas && areaOptions.length === 0 && (
          <div className="w-full text-center text-[13px] font-sans font-medium text-gray-500 py-2">
            No areas found for {city}.
          </div>
        )}

        {displayedAreas.map((area) => (
          <label
            key={area}
            className={`checkbox-button ${selectedAreas.includes(area) ? "checkbox-selected" : ""}`}
          >
            <input
              className="custome-checkbox"
              type="checkbox"
              checked={selectedAreas.includes(area)}
              onChange={() => (selectedAreas.includes(area) ? removeArea(area) : addArea(area))}
            />{" "}
            {area}
          </label>
        ))}

        {areaOptions.length > visibleAreasCount && (
          <div className="w-full flex items-center justify-center mt-2">
            <div className="px-3 py-1 rounded-full border bg-purple-100 border-purple-200 hover:bg-purple-200">
              <button
                type="button"
                onClick={() => setIsShowingAllAreas(!isShowingAllAreas)}
                className="no-underline cursor-pointer flex items-center gap-2"
              >
                {isShowingAllAreas ? (
                  <ChevronUp size={16} className="text-purple-800 shrink-0" />
                ) : (
                  <ChevronDown size={16} className="text-purple-800 shrink-0" />
                )}
                <span className="text-[13px] font-sans font-medium text-gray-800">
                  {isShowingAllAreas ? "Show Less" : "Show More Areas"}
                </span>
                {isShowingAllAreas ? (
                  <ChevronUp size={16} className="text-purple-800 shrink-0" />
                ) : (
                  <ChevronDown size={16} className="text-purple-800 shrink-0" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AreaPicker;
